import { useEffect, useState, useCallback } from 'react';
import {
  cxGet, cxPost, cxPut, fmtNum, fmtMoney, timeAgo, CoverageBanner, useCxEntity, Panel,
  segClass, statusClass, priorityClass, SEG_META, SPECTRUM_META, TIER_META, tierClass,
  CATEGORY_OPTS, PRIORITY_OPTS,
} from './cxShared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import SearchableCashierSelect from '../../components/SearchableCashierSelect';
import SearchableCourseSelect from '../../components/SearchableCourseSelect';
import { MagnifyingGlass, User, ArrowLeft, ArrowRight, PencilSimple, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';

const cap = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const PERF = ['Excellent', 'Good', 'Normal', 'Dormant'];

export default function CxCustomers() {
  const { entityId } = useCxEntity();
  const [data, setData] = useState({ rows: [], total: 0, page: 1, total_pages: 1, coverage_cached: 0, metrics_as_of: null });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: '', performance: 'all', dormancy: 'all', tier: 'all', segment: 'all', sort: 'recency' });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), page_size: '25', sort: f.sort });
      if (entityId && entityId !== 'all') p.set('entity_id', entityId);
      if (f.search) p.set('search', f.search);
      if (f.performance !== 'all') p.set('performance', f.performance);
      if (f.dormancy !== 'all') p.set('dormancy', f.dormancy);
      if (f.tier !== 'all') p.set('tier', f.tier);
      if (f.segment !== 'all') p.set('segment', f.segment);
      setData(await cxGet(`/cx/customers?${p.toString()}`));
    } catch (e) { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [entityId, f, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [entityId, f]);

  return (
    <div className="space-y-2" data-testid="cx-customers-page">
      <CoverageBanner asOf={data.metrics_as_of} cached={data.coverage_cached} total={data.total} onRefreshed={load} />

      <Panel testid="cx-customers-panel">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-[#78716C]">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
              <Input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Name, mobile or card…" className="pl-9 border-[#EDE6D8] rounded-lg h-10" data-testid="customers-search" />
            </div>
          </div>
          <FilterSelect label="Performance" value={f.performance} onChange={(v) => setF({ ...f, performance: v })} opts={PERF.map(p => [p.toLowerCase(), p])} testid="filter-performance" />
          <FilterSelect label="Dormancy" value={f.dormancy} onChange={(v) => setF({ ...f, dormancy: v })} opts={Object.keys(SPECTRUM_META).map(k => [k, `${SPECTRUM_META[k].label}${SPECTRUM_META[k].range !== '—' ? ` (${SPECTRUM_META[k].range})` : ''}`])} testid="filter-dormancy" />
          <FilterSelect label="Tier" value={f.tier} onChange={(v) => setF({ ...f, tier: v })} opts={Object.keys(TIER_META).map(k => [k.toLowerCase(), k])} testid="filter-tier" />
          <FilterSelect label="Segment" value={f.segment} onChange={(v) => setF({ ...f, segment: v })} opts={Object.keys(SEG_META).map(k => [k.toLowerCase(), k])} testid="filter-segment" />
          <FilterSelect label="Sort" value={f.sort} onChange={(v) => setF({ ...f, sort: v })} noAll opts={[['recency', 'Most idle'], ['value', 'Top spenders'], ['refills', 'Most refills'], ['name', 'Name']]} testid="filter-sort" />
        </div>

        <div className="border border-[#EDE6D8] rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead><tr className="bg-[#FCFAF6] text-left">
              {['Customer', 'Campus', 'Tier', 'Segment', 'Refills', 'Lifetime Spend', 'Last Refill', 'Idle', 'Health'].map(h => (
                <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-[#EDE6D8]">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[#78716C]">Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[#78716C]" data-testid="customers-empty">No customers match.</td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.sub_id} className="hover:bg-[#FCFAF6]/60 cursor-pointer" onClick={() => setSelected(r.sub_id)} data-testid={`customer-row-${r.sub_id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#1C1917]">{r.name || '—'}</div>
                    <div className="text-[11px] text-[#78716C] font-mono">{r.mobile || r.card_no || r.sub_id}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-[#78716C] max-w-[140px] truncate">{r.campus_name || '—'}</td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${tierClass(r.tier)}`} data-testid={`customer-tier-${r.sub_id}`}>{r.tier || '—'}</span></td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${segClass(r.segment)}`}>{r.segment}</span></td>
                  <td className="px-3 py-2.5 text-[#44403C]">{fmtNum(r.total_refills)}</td>
                  <td className="px-3 py-2.5 text-[#44403C]">{fmtMoney(r.lifetime_refill_amount, r.currency)}</td>
                  <td className="px-3 py-2.5 text-xs text-[#78716C]">{r.last_refill ? timeAgo(r.last_refill) : '—'}</td>
                  <td className="px-3 py-2.5 text-xs text-[#44403C]">{r.dormancy_days != null ? `${r.dormancy_days}d` : '—'}</td>
                  <td className="px-3 py-2.5 text-xs">{r.performance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-[#78716C]">{fmtNum(data.total)} customers · page {data.page} of {data.total_pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="border-[#EDE6D8] rounded-lg" data-testid="customers-prev"><ArrowLeft size={14} /></Button>
            <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} className="border-[#EDE6D8] rounded-lg" data-testid="customers-next"><ArrowRight size={14} /></Button>
          </div>
        </div>
      </Panel>

      {selected && <Customer360 subId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function FilterSelect({ label, value, onChange, opts, testid, noAll }) {
  return (
    <div className="w-40">
      <Label className="text-xs text-[#78716C]">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="border-[#EDE6D8] rounded-lg h-10" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {!noAll && <SelectItem value="all">All</SelectItem>}
          {opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

const M = ({ label, value, tone }) => (
  <div className="bg-[#FCFAF6] rounded-lg p-3">
    <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold">{label}</p>
    <p className={`text-lg font-bold mt-0.5 ${tone || 'text-[#0A2540]'}`}>{value}</p>
  </div>
);

export function Customer360({ subId, onClose }) {
  const [d, setD] = useState(null);
  const [caseForm, setCaseForm] = useState(null); // null=hidden
  const [editForm, setEditForm] = useState(null);
  const [renewForm, setRenewForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cashiers, setCashiers] = useState([]);
  const [cashiersLoading, setCashiersLoading] = useState(false);
  const [idTypes, setIdTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  const load = useCallback(async () => {
    try { setD(await cxGet(`/cx/customers/${subId}`)); } catch (e) { toast.error('Failed to load customer'); }
  }, [subId]);
  useEffect(() => { load(); }, [load]);

  // Refetch courses when the (student) edit form's campus/category changes — mirrors SubscriberDetailPage.
  useEffect(() => {
    if (editForm && (editForm.category || '').toUpperCase() === 'STUDENT' && editForm.campus_id) {
      cxGet(`/student-courses?campus_id=${editForm.campus_id}`)
        .then((r) => setCourses(Array.isArray(r) ? r : []))
        .catch(() => setCourses([]));
    }
  }, [editForm?.campus_id, editForm?.category]);

  const p = d?.profile; const m = d?.metrics; const cur = m?.currency || '';
  const tail = (x) => String(x || '').replace(/\D/g, '');
  const eligibleCashiers = (cashiers || []).filter(
    (c) => (!p?.entity_id || String(c.entity_id) === String(p.entity_id)) && tail(c.user_mobile).length >= 9,
  );

  const submitCase = async () => {
    if (!caseForm.subject.trim()) { toast.error('Subject required'); return; }
    try {
      await cxPost('/cx/cases', { subscriber_id: p.sub_id, subscriber_name: p.name, subscriber_mobile: p.mobile, ...caseForm });
      toast.success('Case created');
      setCaseForm(null); load();
    } catch (e) { toast.error('Failed to create case'); }
  };

  const apiErr = (e, fb) => e?.response?.data?.detail || fb;

  const ensureCashiers = useCallback(async () => {
    if (cashiers.length > 0) return;
    setCashiersLoading(true);
    const eq = p?.entity_id ? `?entity_id=${encodeURIComponent(p.entity_id)}` : '';
    try { const data = await cxGet(`/cashiers${eq}`); setCashiers(Array.isArray(data) ? data : []); }
    catch (e) { /* leave empty — panel shows "no cashiers" */ }
    finally { setCashiersLoading(false); }
  }, [cashiers.length, p?.entity_id]);

  const openEdit = async () => {
    setRenewForm(null);
    setEditLoading(true);
    // Seed immediately from the 360 profile so the panel isn't blank while loading.
    setEditForm({
      sub_name: p.name || '', mobile: p.mobile || '', mobile_2: '', email: p.email || '',
      gender: 'M', category: '', student_id: '', student_course_id: '', curr_year_of_study: '1',
      id_type: '1', id_number: '', relationship_agent: '', campus_id: '',
    });
    ensureCashiers();
    const eq = p.entity_id ? `?entity_id=${encodeURIComponent(p.entity_id)}` : '';
    const [cats, ids, camps, canon] = await Promise.all([
      cxGet('/subscriber-categories').catch(() => []),
      cxGet('/subscriber-id-types').catch(() => []),
      cxGet(`/campuses${eq}`).catch(() => []),
      cxGet(`/subscribers/${p.sub_id}`).catch(() => null),
    ]);
    setCategories(Array.isArray(cats) ? cats : []);
    setIdTypes(Array.isArray(ids) ? ids : []);
    setCampuses(Array.isArray(camps) ? camps : []);
    if (canon) {
      setEditForm({
        sub_name: canon.sub_name || '', mobile: canon.mobile || '', mobile_2: canon.mobile_2 || '',
        email: canon.email || '', gender: canon.gender || 'M', category: canon.category || '',
        student_id: canon.student_id || '', student_course_id: canon.student_course_id || '',
        curr_year_of_study: canon.curr_year_of_study || '1', id_type: String(canon.id_type || '1'),
        id_number: canon.id_number || '', relationship_agent: canon.relationship_agent || '',
        campus_id: canon.campus_id ? String(canon.campus_id) : '',
      });
    }
    setEditLoading(false);
  };

  const saveEdit = async () => {
    if (!editForm.sub_name?.trim()) { toast.error('Full name is required'); return; }
    if (!editForm.mobile?.trim()) { toast.error('Mobile is required'); return; }
    if (!editForm.category) { toast.error('Category is required'); return; }
    if (!editForm.campus_id) { toast.error('Campus is required'); return; }
    const payload = {
      sub_name: editForm.sub_name,
      mobile: editForm.mobile,
      mobile_2: editForm.mobile_2 || '',
      email: editForm.email || '',
      gender: editForm.gender || 'N/A',
      campus_id: editForm.campus_id,
      category: editForm.category,
      student_id: editForm.student_id || '',
      student_course_id: editForm.student_course_id || '',
      curr_year_of_study: editForm.curr_year_of_study || '',
      id_type: editForm.id_type || '1',
      id_number: editForm.id_number || '',
      relationship_agent: editForm.relationship_agent || '',
    };
    setBusy(true);
    try {
      await cxPut(`/subscribers/${p.sub_id}`, payload);
      toast.success('Customer details updated');
      setEditForm(null); await load();
    } catch (e) { toast.error(apiErr(e, 'Update failed')); }
    finally { setBusy(false); }
  };

  const openRenew = async () => {
    setEditForm(null);
    setRenewForm({ new_card: '', reason: 'Lost', plan: 'Fee', description: '', agent_id: '' });
    ensureCashiers();
  };
  const submitRenew = async () => {
    if (!renewForm.new_card.trim()) { toast.error('New card number is required'); return; }
    if (!renewForm.agent_id) { toast.error('Please select the cashier handling the renewal'); return; }
    setBusy(true);
    try {
      await cxPost(`/subscribers/${p.sub_id}/renew-card`, {
        new_card: renewForm.new_card.trim(), reason: renewForm.reason, plan: renewForm.plan,
        description: renewForm.description || '', agent_id: renewForm.agent_id,
      });
      toast.success('Card renewed');
      setRenewForm(null); await load();
    } catch (e) { toast.error(apiErr(e, 'Card renewal failed')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="customer-360">
        {!d ? (
          <>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Customer profile</DialogTitle>
              <DialogDescription>Loading customer details…</DialogDescription>
            </DialogHeader>
            <p className="py-10 text-center text-[#78716C]">Loading…</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <User size={20} className="text-[#0A2540]" /> {p.name || 'Customer'}
              </DialogTitle>
              <DialogDescription>
                <span className="font-mono">{p.mobile || '—'}</span> · {p.campus_name || '—'} · Card {p.card_no || '—'} · {p.category || '—'}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${segClass('Champion')}`} style={{ background: '#0A2540', color: '#fff' }}>{m.lifecycle_stage}</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FCFAF6] border border-[#EDE6D8] text-[#78716C]">Health: {p.performance}</span>
              {m.tier && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${tierClass(m.tier)}`} data-testid="c360-tier">{m.tier} tier</span>}
              {m.spectrum && SPECTRUM_META[m.spectrum] && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: (SPECTRUM_META[m.spectrum]?.color || '#A8A29E') + '22', color: SPECTRUM_META[m.spectrum]?.color }} data-testid="c360-spectrum">{SPECTRUM_META[m.spectrum]?.label}{SPECTRUM_META[m.spectrum]?.range && SPECTRUM_META[m.spectrum].range !== '—' ? ` · ${SPECTRUM_META[m.spectrum].range}` : ''}</span>}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <Button size="sm" variant="outline" onClick={openEdit} className="border-[#EDE6D8] rounded-lg h-8 text-[#0A2540]" data-testid="c360-edit-btn"><PencilSimple size={14} className="mr-1.5" />Edit details</Button>
              <Button size="sm" variant="outline" onClick={openRenew} className="border-[#EDE6D8] rounded-lg h-8 text-[#0A2540]" data-testid="c360-renew-btn"><CreditCard size={14} className="mr-1.5" />Renew card</Button>
            </div>

            {editForm && (
              <div className="border border-[#EDE6D8] rounded-lg p-4 mb-4 space-y-3 bg-[#FCFAF6]" data-testid="c360-edit-panel">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#0A2540]">Edit customer details</p>
                  {editLoading && <span className="text-[11px] text-[#78716C]">Loading…</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Full name *</Label><Input value={editForm.sub_name} onChange={(e) => setEditForm({ ...editForm, sub_name: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-name" /></div>
                  <div><Label className="text-xs">Mobile *</Label><Input value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-mobile" /></div>
                  <div><Label className="text-xs">Alt. mobile</Label><Input value={editForm.mobile_2} onChange={(e) => setEditForm({ ...editForm, mobile_2: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-alt-mobile" /></div>
                  <div><Label className="text-xs">Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-email" /></div>
                  <div><Label className="text-xs">Gender</Label>
                    <div className="flex gap-6 pt-2">
                      {['M', 'F'].map((g) => (
                        <label key={g} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="radio" name="cx-edit-gender" checked={editForm.gender === g} onChange={() => setEditForm({ ...editForm, gender: g })} className="accent-[#0A2540]" data-testid={`edit-gender-${g.toLowerCase()}`} />
                          {g === 'M' ? 'Male' : 'Female'}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div><Label className="text-xs">Category *</Label>
                    <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-category"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">ID Type</Label>
                    <Select value={editForm.id_type} onValueChange={(v) => setEditForm({ ...editForm, id_type: v })}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-id-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{idTypes.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.id_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">ID Number</Label><Input value={editForm.id_number} onChange={(e) => setEditForm({ ...editForm, id_number: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-id-number" /></div>
                  <div><Label className="text-xs">Campus *</Label>
                    <Select value={editForm.campus_id} onValueChange={(v) => setEditForm({ ...editForm, campus_id: v })}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-campus"><SelectValue placeholder="Select campus" /></SelectTrigger>
                      <SelectContent>{campuses.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.campus_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {(editForm.category || '').toUpperCase() === 'STUDENT' && (
                    <>
                      <div><Label className="text-xs">Student ID</Label><Input value={editForm.student_id} onChange={(e) => setEditForm({ ...editForm, student_id: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-student-id" /></div>
                      <div><Label className="text-xs">Course</Label>
                        <SearchableCourseSelect value={editForm.student_course_id} onChange={(v) => setEditForm({ ...editForm, student_course_id: v })} courses={courses} placeholder="Select course" testid="edit-course" />
                      </div>
                      <div><Label className="text-xs">Year of study</Label>
                        <Select value={editForm.curr_year_of_study} onValueChange={(v) => setEditForm({ ...editForm, curr_year_of_study: v })}>
                          <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9" data-testid="edit-year"><SelectValue /></SelectTrigger>
                          <SelectContent>{['1', '2', '3', '4', '5', '6', '7'].map((y) => <SelectItem key={y} value={y}>Year {y}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="sm:col-span-2"><Label className="text-xs">Relationship agent</Label>
                    <SearchableCashierSelect value={editForm.relationship_agent} onChange={(v) => setEditForm({ ...editForm, relationship_agent: v })} cashiers={eligibleCashiers} placeholder={cashiersLoading ? 'Loading cashiers…' : 'Select cashier'} testid="edit-rel-agent" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditForm(null)} className="border-[#EDE6D8] rounded-lg h-8">Cancel</Button>
                  <Button size="sm" onClick={saveEdit} disabled={busy || editLoading} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-8" data-testid="c360-edit-save">{busy ? 'Saving…' : 'Save'}</Button>
                </div>
              </div>
            )}

            {renewForm && (
              <div className="border border-[#EDE6D8] rounded-lg p-4 mb-4 space-y-3 bg-[#FCFAF6]" data-testid="c360-renew-panel">
                <p className="text-sm font-semibold text-[#0A2540]">Renew card</p>
                <p className="text-xs text-[#78716C]">Current card: <span className="font-mono text-[#44403C]">{p.card_no || '—'}</span></p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">New card number *</Label><Input value={renewForm.new_card} onChange={(e) => setRenewForm({ ...renewForm, new_card: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" data-testid="renew-card" /></div>
                  <div><Label className="text-xs">Reason *</Label>
                    <Select value={renewForm.reason} onValueChange={(v) => setRenewForm({ ...renewForm, reason: v })}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9" data-testid="renew-reason"><SelectValue /></SelectTrigger>
                      <SelectContent>{['Lost', 'Damaged', 'malfunction'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Plan</Label>
                    <Select value={renewForm.plan} onValueChange={(v) => setRenewForm({ ...renewForm, plan: v })}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9" data-testid="renew-plan"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Fee">Fee (paid)</SelectItem><SelectItem value="Free">Free</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label className="text-xs">Cashier handling renewal *</Label>
                    <SearchableCashierSelect value={renewForm.agent_id} onChange={(v) => setRenewForm({ ...renewForm, agent_id: v })} cashiers={eligibleCashiers} placeholder={cashiersLoading ? 'Loading cashiers…' : 'Select cashier'} testid="renew-agent" />
                  </div>
                  <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input value={renewForm.description} onChange={(e) => setRenewForm({ ...renewForm, description: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9" /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setRenewForm(null)} className="border-[#EDE6D8] rounded-lg h-8">Cancel</Button>
                  <Button size="sm" onClick={submitRenew} disabled={busy} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-8" data-testid="c360-renew-submit">{busy ? 'Renewing…' : 'Renew card'}</Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <M label="Wallet Balance" value={fmtMoney(p.wallet_balance, cur)} />
              <M label="Total Refills" value={fmtNum(m.total_refills)} />
              <M label="Lifetime Refill Spend" value={fmtMoney(m.lifetime_refill_amount, cur)} />
              <M label="Lifetime Top-ups" value={`${fmtMoney(m.lifetime_topup_amount, cur)} · ${m.topup_count}`} />
              <M label="Last Refill" value={m.last_refill || '—'} />
              <M label="Last Top-up" value={m.last_topup || '—'} />
              <M label="Days Idle" value={m.dormancy_days != null ? `${m.dormancy_days}d` : '—'} tone="text-[#0A2540]" />
              <M label="Registered" value={p.registered || '—'} />
            </div>

            <Tabs defaultValue="activity">
              <TabsList className="bg-[#FCFAF6]">
                <TabsTrigger value="activity" data-testid="c360-tab-activity">Activity</TabsTrigger>
                <TabsTrigger value="messages" data-testid="c360-tab-messages">Messages ({d.messages.length})</TabsTrigger>
                <TabsTrigger value="cases" data-testid="c360-tab-cases">Cases ({d.cases.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="activity" className="mt-3">
                {d.transactions.length === 0 ? <p className="text-sm text-[#78716C] py-6 text-center">No recent activity.</p> : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {d.transactions.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-[#F4F0E8] pb-1.5">
                        <div>
                          <span className={`font-semibold ${t.type === 'refill' ? 'text-[#78716C]' : 'text-[#0A2540]'}`}>{t.type === 'refill' ? '−' : '+'}{fmtMoney(Math.abs(t.amount), cur)}</span>
                          <span className="text-[#78716C] ml-2 text-xs">{t.narration}</span>
                        </div>
                        <span className="text-[11px] text-[#A8A29E] font-mono">{t.date}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="messages" className="mt-3">
                {d.messages.length === 0 ? <p className="text-sm text-[#78716C] py-6 text-center">No messages sent.</p> : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {d.messages.map((msg, i) => (
                      <div key={i} className="bg-[#FCFAF6] rounded-lg p-2.5 text-sm">
                        <p className="text-[#1C1917]">{msg.message}</p>
                        <p className="text-[10px] text-[#78716C] mt-1">{msg.status} · {timeAgo(msg.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="cases" className="mt-3">
                <div className="flex justify-end mb-2">
                  <Button size="sm" onClick={() => setCaseForm({ subject: '', category: 'other', priority: 'medium', description: '' })} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-8" data-testid="c360-log-case">Log a case</Button>
                </div>
                {caseForm && (
                  <div className="border border-[#EDE6D8] rounded-lg p-3 mb-3 space-y-2">
                    <Input placeholder="Subject" value={caseForm.subject} onChange={(e) => setCaseForm({ ...caseForm, subject: e.target.value })} className="border-[#EDE6D8] rounded-lg" data-testid="c360-case-subject" />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={caseForm.category} onValueChange={(v) => setCaseForm({ ...caseForm, category: v })}><SelectTrigger className="border-[#EDE6D8] rounded-lg h-9"><SelectValue /></SelectTrigger><SelectContent>{CATEGORY_OPTS.map(o => <SelectItem key={o} value={o}>{cap(o)}</SelectItem>)}</SelectContent></Select>
                      <Select value={caseForm.priority} onValueChange={(v) => setCaseForm({ ...caseForm, priority: v })}><SelectTrigger className="border-[#EDE6D8] rounded-lg h-9"><SelectValue /></SelectTrigger><SelectContent>{PRIORITY_OPTS.map(o => <SelectItem key={o} value={o}>{cap(o)}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setCaseForm(null)} className="border-[#EDE6D8] rounded-lg h-8">Cancel</Button>
                      <Button size="sm" onClick={submitCase} className="bg-[#0A2540] text-white rounded-lg h-8" data-testid="c360-case-submit">Create</Button>
                    </div>
                  </div>
                )}
                {d.cases.length === 0 ? <p className="text-sm text-[#78716C] py-6 text-center">No cases logged.</p> : (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {d.cases.map((c) => (
                      <div key={c.case_number} className="flex items-center justify-between bg-[#FCFAF6] rounded-lg p-2.5 text-sm">
                        <div><span className="font-mono text-xs text-[#0A2540]">{c.case_number}</span> <span className="text-[#1C1917]">{c.subject}</span></div>
                        <div className="flex gap-1.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${priorityClass(c.priority)}`}>{cap(c.priority)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusClass(c.status)}`}>{cap(c.status)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
